//node-modules
import {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
// common
import { Common } from "../../common/src/index";
//src
import NavBar from "../components/NavBar";
import ProjectList from "../components/Home/ProjectList";
import MenuButton from "../components/Home/MenuButton";
import "../../css/buttonTheme.css";
import "../../css/formTheme.css";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Grid,
  styled,
  useTheme,
} from "@mui/material";
import Clusterings from "../components/Utility/Clusterings";
import "../locales/i18n";
import { config } from "../config/config";

// mui
import { makeStyles } from "@material-ui/core";

import { MsgSnackbar } from "../components/MsgSnackbar";
import { MsgErrSnackbar } from "../components/MsgErrSnackbar";
import { createSearchParams, Navigate, useLocation } from "react-router-dom";
import MsgWarnSnackbar from "../components/MsgWarnSnackbar";
import { HeaderFilter } from "../components/Home/HeaderFilter";
import { ProjectUserList } from "../../common/src/modules";
import { FilterDate } from "../components/Home/parts/FilterDate";
import { convertStatusStr, fetchProjectList } from "../api/project";
import GroupToggleButton from "../components/Home/parts/GroupToggleButton";
import SearchResult from "../components/Home/parts/SearchResult";
import { fetchUserLicense } from "../api/user";
import { UserSiteContext } from "../..";
import ViewOptionsButton from "../components/Home/parts/ViewOptionsButton";
import ProjectListTableHeader from "../components/Home/parts/ProjectListTableHeader";
import UploadBaseDialog, {
  UploadBaseDialogHandle,
} from "../components/UploadBaseDialog";
import RenameProjectDialog from "../components/RenameProjectDialog";
import DeleteProjectDialog from "../components/DeleteProjectDialog";

export type testContextType = {
  isDelete: boolean;
  setIsDelete: React.Dispatch<React.SetStateAction<boolean>>;
  getProjectList?: (abortController: AbortController) => void;
  removeProjectsFromList?: (ids: string[]) => void;
};

export const deleteContext = createContext<testContextType>({
  isDelete: false,
  setIsDelete: () => {},
  getProjectList: (abortController: AbortController) => {},
  removeProjectsFromList: (ids: string[]) => {},
});

export type UploadDialogContextType = {
  openUploadDialog: (project: Common.Project) => void;
};

export const uploadDialogContext = createContext<UploadDialogContextType>({
  openUploadDialog: () => {},
});

export type RenameDialogContextType = {
  openRenameDialog: (project: Common.Project) => void;
};

export const renameDialogContext = createContext<RenameDialogContextType>({
  openRenameDialog: () => {},
});

export type DeleteDialogContextType = {
  openDeleteDialog: (project: Common.Project) => void;
};

export const deleteDialogContext = createContext<DeleteDialogContextType>({
  openDeleteDialog: () => {},
});

export const enum EGroup {
  YEAR = "year",
  MONTH = "month",
  DAY = "day",
}

export const enum EViewType {
  GRID = "grid",
  LIST = "list",
}

const Home = () => {
  const location = useLocation();

  /**
   * 保持するプロジェクトのデータ構造
   * >> プロジェクトが分割・絞り込みされていく >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
   * (All)         (folder)                     (filtered)           (clustering)
   * projectList -- folderProjectList -- filterdProjectList -- displayProjects[date1]
   *                                                            |- …
   * <-不変-------> <-可変------------------------------------------------------------>
   */
  const { t, i18n } = useTranslation();
  document.title = t("LIST_PROJECT");

  const { userInfo, userList, jobsiteList } = useContext(UserSiteContext);

  // check
  const [projectList, setProjectList] = useState<Common.Project[]>([]);
  const [selectProject, setSelectProject] = useState<Common.Project[]>([]);

  const [group, setGroup] = useState<EGroup>(EGroup.MONTH); // sep, alignment -> group
  const [viewType, setViewType] = useState<EViewType>(EViewType.GRID);
  const [sortBy, setSortBy] = useState<string>("create");
  const [numHits, setNumHits] = useState<number>(0);
  const [numTotal, setNumTotal] = useState<number>(0);
  const [isLoadingList, setIsLoadingList] = useState<boolean>(false);

  // Memoize displayProjects để tránh re-cluster khi chỉ viewType thay đổi.
  // Truyền sortBy để gom nhóm theo đúng field ngày đang chọn (create=captureTime,
  // upload=uploadTime) — nếu không, list luôn gom theo captureTime khiến project
  // mới upload bị vùi vào nhóm ngày chụp cũ (SC_QUICK-1975).
  const displayProjects = useMemo(() => {
    if (projectList.length === 0) return {};
    return Clusterings.clusterByDate(projectList, group, sortBy);
  }, [projectList, group, sortBy]);

  // Snackbar
  const [errorSnackbar, setErrorSnackbar] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [snackbar, setSnackbar] = useState(false);
  const [msg] = useState("");
  //プロジェクト削除有効化フラグ
  const [isDelete, setIsDelete] = useState(false);
  //language
  const [isLanguage, setLanguage] = useState<boolean>(false);
  //　ユーザー情報
  // 警告
  const [openWarnig, setOpenWarning] = useState<boolean>(false);
  const [msgWarning, setMsgWarning] = useState<string>("");

  const [openLicenseDialog, setOpenLicenseDialog] = useState(false);

  const [isARAvailable, setIsARAvailable] = useState<boolean>(false);

  // UploadBaseDialog owns its own open/close + project state (bug-2094-v4) — Home
  // only needs a ref to imperatively request opening it for a given project.
  const uploadDialogRef = useRef<UploadBaseDialogHandle>(null);
  const openUploadDialog = useCallback((project: Common.Project) => {
    uploadDialogRef.current?.open(project);
  }, []);

  // Shared RenameProjectDialog state (lifted from Project component)
  const [renameTargetProject, setRenameTargetProject] =
    useState<Common.Project | null>(null);
  const openRenameDialog = useCallback((project: Common.Project) => {
    setRenameTargetProject(project);
  }, []);

  // Shared DeleteProjectDialog state (lifted from Project component)
  const [deleteTargetProject, setDeleteTargetProject] =
    useState<Common.Project | null>(null);
  const openDeleteDialog = useCallback((project: Common.Project) => {
    setDeleteTargetProject(project);
  }, []);

  /** useEffect */
  useEffect(() => {
    // license check
    const hasLicense = CheckLicense();
    if (!hasLicense) return;
  }, []);

  useEffect(() => {
    if (i18n.language === "ja") setLanguage(true);
    else setLanguage(false);
  }, [i18n.language]);

  useEffect(() => {
    FireGA(userInfo);
  }, [userInfo]);

  useEffect(() => {
    const controller = new AbortController();
    getProjectList(controller);
    return () => {
      controller.abort();
    };
  }, [location.search, location.pathname, sortBy]);

  const FireGA = (uInfo: Common.LandlogNecessaryUserInfo) => {
    const { dataLayer } = window;
    const areAllNotNull = (vals: any[]): boolean => {
      return vals.every((val) => val !== null && val !== undefined);
    };
    if (areAllNotNull([uInfo?.corporationId, uInfo?.userId])) {
      dataLayer.push({
        sc_corporation_id: uInfo?.corporationId,
        sc_user_id: uInfo?.userId,
      });

      dataLayer.push({
        event: "sc_open",
      });
    }
  };

  const CheckLicense = async () => {
    let hasLicense = false;
    try {
      const data = await fetchUserLicense();
      const landlogLicenseStatus: Common.LandlogLicenseStatus = data;
      console.log(data);
      const numOfLicenses = landlogLicenseStatus.numOfLicenses;
      setIsARAvailable(landlogLicenseStatus.isARAvailable);
      if (config.licenseCheck) {
        if (1 <= numOfLicenses) {
          hasLicense = true;
        } else if (0 < numOfLicenses) {
          hasLicense = true;
          setOpenLicenseDialog(true);
        } else {
          throw new Error();
        }
      }
    } catch {
      if (config.licenseCheck === true) {
        setErrorSnackbar(true);
        setErrorMsg(t("LICENSE_ERROR"));
        setTimeout(function () {
          window.location.href = `${config.serverHost}/login?type=web`;
        }, 2000);
      }
    } finally {
      return hasLicense;
    }
  };

  const handleCloseLicenseDialog = () => {
    setOpenLicenseDialog(false);
  };

  const getProjectList = async (controller: AbortController) => {
    try {
      // if location.search don't have any search params, don't call api
      if (!location.search) return;

      setIsLoadingList(true);
      const params = new URLSearchParams(location.search);
      const statusid = params.get("status");
      const userid = params.get("userid");
      const jobsiteid = params.get("jobsiteid");
      const searchstr = params.get("search");
      const rtk = !params.get("rtk") ? 2 : params.get("rtk") === "true" ? 1 : 0;
      const remove = !params.get("remove") ? false : true;
      const from = params.get("from");
      const to = params.get("to");
      const data = await fetchProjectList(controller, {
        status: convertStatusStr(statusid),
        userId: userid !== "all" && userid,
        jobsiteId: jobsiteid,
        searchStr: searchstr,
        isRTK: rtk,
        isPointRemoved: remove,
        from: from,
        to: to,
        sortBy: sortBy,
      });
      setProjectList(data.projects);
      setNumHits(data.hitsTotal);
      setNumTotal(data.total);
    } catch (e) {
      console.error("Error: " + e);
    } finally {
      setIsLoadingList(false);
    }
  };

  const removeProjectsFromList = (ids: string[]) => {
    setProjectList((prev) => Clusterings.excludeByIds(prev, ids));
  };

  //スナックバー機能
  const handleErrorSnackbarClose = async () => {
    setErrorSnackbar(false);
  };
  const handleSnackbarClose = async () => {
    setSnackbar(false);
  };

  // 警告
  const handleWarnCloseSnackbar = () => {
    setOpenWarning(false);
  };

  const theme = useTheme();

  const customColor = {
    color: theme.palette.base.contrast,
    background: theme.palette.base.main,
  };

  const customDialogBackgroundColor = {
    color: theme.palette.base.dialogBackground,
  };

  const customBackgroundStyles = {
    background: theme.palette.base.modal,
  };

  // MuiPaper-root css
  const useStyles = makeStyles(() => ({
    root: {
      "&.MuiPaper-root,&.MuiPaper-elevation": {
        color: "#fff",
        backgroundcolor: "#121212",
      },
    },
  }));

  const classes = useStyles();

  // bodyのスタイルの上書き
  const useBodyStyles = makeStyles(() => ({
    body: {
      background: theme.palette.base.sub,
    },
  }));

  const classesBody = useBodyStyles();
  document.body.classList.add(classesBody.body);

  const cssStyles = makeStyles({
    dialogRoot: {
      "&.MuiTypography-root": { textAlign: "left", fontSize: "18px" },
    },
    btn1: {
      "&.MuiButtonBase-root": {
        height: "24px",
        width: "24px",
        color: customColor.color,
        float: "right",
        fontSize: "18px",
        margin: "0px",
        padding: "0px",
        position: "absolute",
        right: "0px",
        outline: "none",
      },
    },
  });
  const userStyle = cssStyles();

  const CustomButton = styled(Button)({
    background: theme.palette.primary.main,
    boxShadow: "none",
    color: "#FFFFFF",
    height: "42px",
    width: "250px",
    outline: "none",
    textTransform: "none",
    "&:hover": {
      background: theme.palette.primary.main,
      boxShadow: "none",
    },
  });

  const createFilterUserList = (
    userList: ProjectUserList[],
    additions?: { value: string; label: string }[],
  ) => {
    const convertedList = userList.map((item) => {
      return { value: item.userId, label: item.name };
    });
    const filteredList = convertedList.filter((item) => {
      return item.label;
    });
    return [...additions, ...filteredList];
  };

  const createFilterJobsiteList = (
    jobsiteList: Common.LandlogJobSite[],
    additions?: { value: string; label: string }[],
  ) => {
    const convertedList = jobsiteList.map((item) => {
      return { value: item.id, label: item.name };
    });
    const filteredList = convertedList.filter((item) => {
      return item.label;
    });
    return [...additions, ...filteredList];
  };

  const search = window.location.search;
  const params = new URLSearchParams(search);
  let init = true;
  // 検索条件がなければuserIDがデフォルト
  const withoutParams = useMemo(() => {
    return (
      !params.get("status") &&
      !params.get("userid") &&
      !params.get("jobsiteid") &&
      !params.get("search") &&
      !params.get("rtk") &&
      !params.get("remove") &&
      !params.get("from") &&
      !params.get("to")
    );
  }, [params]);

  return withoutParams ? (
    <>
      <Navigate
        to={{
          pathname: "/",
          search: createSearchParams({
            userid: userInfo?.userId,
          }).toString(),
        }}
      />
    </>
  ) : (
    <uploadDialogContext.Provider value={{ openUploadDialog }}>
      {" "}
      <renameDialogContext.Provider value={{ openRenameDialog }}>
        <deleteDialogContext.Provider value={{ openDeleteDialog }}>
          {" "}
          <div
            className={`${classes.root}`}
            style={{
              height: "100vh",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Grid
              container
              sx={{
                zIndex: "10",
                flexShrink: 0,
                // boxShadow:
                // "0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)",
              }}
            >
              <NavBar userInfo={userInfo} />
              <Grid container sx={{ ...customColor, padding: "4px 16px" }}>
                <Grid
                  item
                  xs={11}
                  sx={{
                    display: "flex",
                    alignItems: "flex-end",
                    gap: "var(--space-space--8, 8px)",
                    flexWrap: "wrap",
                  }}
                >
                  <HeaderFilter
                    userList={createFilterUserList(userList, [
                      { value: "all", label: t("ALL") },
                    ])}
                    jobsiteList={createFilterJobsiteList(jobsiteList, [
                      { value: "all", label: t("ALL") },
                    ])}
                    isARAvailable={isARAvailable}
                  />
                </Grid>
                <Grid
                  item
                  xs={1}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    height: "48px",
                    gap: "var(--space-space--8, 8px)",
                  }}
                >
                  <deleteContext.Provider
                    value={{
                      isDelete,
                      setIsDelete,
                      getProjectList,
                      removeProjectsFromList,
                    }}
                  >
                    <MenuButton
                      userInfo={userInfo}
                      projectID={selectProject}
                      setErrorSnackbar={setErrorSnackbar}
                      setErrorMsg={setErrorMsg}
                      setOpenWarning={setOpenWarning}
                      setMsgWarning={setMsgWarning}
                      isARAvailable={isARAvailable}
                    />
                  </deleteContext.Provider>
                </Grid>
              </Grid>
              <Grid
                container
                className="Bottom-bar"
                sx={{
                  padding: "16px",
                  backgroundColor: theme.palette.background.default,
                }}
              >
                <Grid
                  item
                  xs={4}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    height: "48px",
                    gap: "var(--space-space--8, 8px)",
                  }}
                >
                  <SearchResult hits={numHits} total={numTotal} />
                </Grid>
                <Grid
                  item
                  xs={8}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    height: "48px",
                    gap: "var(--space-space--8, 8px)",
                  }}
                >
                  <FilterDate sortBy={sortBy} setSortBy={setSortBy} />
                  <GroupToggleButton
                    value={group}
                    onChange={(value) => {
                      setGroup(value);
                    }}
                  />
                  <ViewOptionsButton
                    value={viewType}
                    onChange={(viewType) => {
                      setViewType(viewType);
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid
              container
              sx={{
                backgroundColor: theme.palette.background.default,
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                position: "relative",
              }}
            >
              {isLoadingList && (
                <Grid
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 3,
                    pointerEvents: "none",
                  }}
                >
                  <CircularProgress size={40} />
                </Grid>
              )}
              <Grid
                container
                sx={{
                  padding: "0px 16px",
                  alignContent: "flex-start",
                }}
              >
                {viewType == EViewType.LIST && (
                  <Grid
                    sx={{
                      position: "sticky",
                      top: 0,
                      zIndex: 2,
                      width: "100%",
                    }}
                  >
                    <ProjectListTableHeader sortBy={sortBy} />
                  </Grid>
                )}
                <deleteContext.Provider
                  value={{ isDelete, setIsDelete, getProjectList }}
                >
                  <ProjectList
                    displayProjects={displayProjects}
                    selectProject={selectProject}
                    setSelectProject={setSelectProject}
                    isLanguage={isLanguage}
                    userList={userList}
                    jobsiteList={jobsiteList}
                    setErrorSnackbar={setErrorSnackbar}
                    setErrorMsg={setErrorMsg}
                    setOpenWarning={setOpenWarning}
                    setMsgWarning={setMsgWarning}
                    isARAvailable={isARAvailable}
                    viewType={viewType}
                    sortBy={sortBy}
                  />
                </deleteContext.Provider>
              </Grid>
            </Grid>
            <Dialog
              open={openLicenseDialog}
              onClose={handleCloseLicenseDialog}
              PaperProps={{
                style: {
                  width: "581px",
                  // height: "500px",
                  boxShadow: "none",
                  border: "1px solid #44444F",
                  ...customBackgroundStyles,
                },
              }}
              slotProps={{
                backdrop: {
                  sx: {
                    backgroundColor: customDialogBackgroundColor.color,
                  },
                },
              }}
            >
              <DialogTitle className={userStyle.dialogRoot}>
                {t("LICENSE_ALERT")}
                <Button
                  className={userStyle.btn1}
                  onClick={handleCloseLicenseDialog}
                >
                  ×
                </Button>
                <div style={{ borderBottom: "1px solid" }} />
              </DialogTitle>
              <DialogContent>
                <DialogTitle style={{ fontSize: "16px" }}>
                  {t("LICENSE_ALERT_CONTENT_1")}
                </DialogTitle>
                <DialogTitle style={{ fontSize: "16px" }} textAlign={"center"}>
                  <a
                    target="_blank"
                    style={{ color: customColor.color }}
                    href={process.env.REACT_APP_SC_LICENSE_URL}
                  >
                    {t("LICENSE_ALERT_CONTENT_2")}
                  </a>
                </DialogTitle>
                <DialogTitle style={{ fontSize: "16px" }}>
                  {t("LICENSE_ALERT_CONTENT_3")}
                </DialogTitle>
                <DialogActions
                  style={{ display: "flex", justifyContent: "center" }}
                >
                  <CustomButton
                    onClick={handleCloseLicenseDialog}
                    variant="contained"
                    size="small"
                    style={{
                      borderRadius: "0px",
                    }}
                  >
                    {t("CLOSE")}
                  </CustomButton>
                </DialogActions>
              </DialogContent>
            </Dialog>

            <MsgSnackbar
              open={snackbar}
              onClose={handleSnackbarClose}
              msg={msg}
            />
            <MsgErrSnackbar
              open={errorSnackbar}
              onClose={handleErrorSnackbarClose}
              msg={errorMsg}
            />
            <MsgWarnSnackbar
              open={openWarnig}
              onClose={handleWarnCloseSnackbar}
              msg={msgWarning}
            />
            {/* Shared UploadBaseDialog – single persistent instance, opened
                imperatively via ref (bug-2094-v4) so it owns its own open/close
                state and never depends on a parent-tracked "target project". */}
            {isARAvailable && (
              <UploadBaseDialog
                ref={uploadDialogRef}
                setErrorSnackbar={setErrorSnackbar}
                setErrorMsg={setErrorMsg}
                setOpenWarning={setOpenWarning}
                setMsgWarning={setMsgWarning}
              />
            )}
            {/* Shared RenameProjectDialog – single instance for all projects */}
            {renameTargetProject && (
              <RenameProjectDialog
                projectID={renameTargetProject.id}
                projectName={renameTargetProject.name}
                setProjectName={(nameOrUpdater) => {
                  const name: string =
                    typeof nameOrUpdater === "function"
                      ? nameOrUpdater(renameTargetProject.name)
                      : nameOrUpdater;
                  setProjectList((prev) =>
                    prev.map((p) =>
                      p.id === renameTargetProject.id ? { ...p, name } : p,
                    ),
                  );
                }}
                openDialog={true}
                setOpenDialog={(open) => {
                  if (!open) setRenameTargetProject(null);
                }}
              />
            )}
            {/* Shared DeleteProjectDialog – single instance for all projects.
                Wrapped in deleteContext.Provider so the dialog's getProjectList()
                resolves to the real refetch instead of the no-op default
                (SC_QUICK-1975: single-project delete didn't refresh the list). */}
            {deleteTargetProject && (
              <deleteContext.Provider
                value={{
                  isDelete,
                  setIsDelete,
                  getProjectList,
                  removeProjectsFromList,
                }}
              >
                <DeleteProjectDialog
                  projectID={deleteTargetProject.id}
                  projectName={deleteTargetProject.name}
                  openDialog={true}
                  setOpenDialog={(open) => {
                    if (!open) setDeleteTargetProject(null);
                  }}
                />
              </deleteContext.Provider>
            )}
          </div>
        </deleteDialogContext.Provider>
      </renameDialogContext.Provider>
    </uploadDialogContext.Provider>
  );
};

export default Home;
